import _ from 'lodash'
import { withDependencies, optional, named } from '@wix/thunderbolt-ioc'
import type { ViewMode, BrowserWindow, ICyclicTabbing, IStructureAPI } from '@wix/thunderbolt-symbols'
import { ViewModeSym, BrowserWindowSymbol, PageFeatureConfigSymbol, StructureAPI } from '@wix/thunderbolt-symbols'
import { DomSelectorsSymbol } from 'feature-dom-selectors'
import type { IDomSelectors } from 'feature-dom-selectors'
import { isSSR } from '@wix/thunderbolt-commons'
import type { IAnimations } from 'feature-animations'
import { Animations } from 'feature-animations'
import { CyclicTabbingSymbol } from 'feature-cyclic-tabbing'
import type { ISiteScrollBlocker } from 'feature-site-scroll-blocker'
import { SiteScrollBlockerSymbol } from 'feature-site-scroll-blocker'
import { AnimationHandler } from './AnimationHandler'
import { MenuContainerHooksSymbol, name } from './symbols'
import type { ComponentDriverFactory } from 'feature-components'
import type {
	MenuContainerComponent,
	MenuContainerPageConfig,
	AnimationData,
	MenuContainerDriver,
	IMenuContainerHooks,
} from './types'

const menuContainerDriverFactory = (
	// Read only when the menu is used, never at construction or driver creation: in the editor this
	// factory is bound in the root container, whose page config is a stable object that stays empty
	// until the first navigation completes (components are created before that) and is replaced in
	// place whenever the current page changes.
	pageConfig: MenuContainerPageConfig,
	viewMode: ViewMode,
	window: BrowserWindow,
	cyclicTabbing: ICyclicTabbing,
	structureApi: IStructureAPI,
	domSelectors: IDomSelectors,
	siteScrollBlocker?: ISiteScrollBlocker,
	animations?: IAnimations,
	menuContainerHooks?: IMenuContainerHooks
): ComponentDriverFactory<MenuContainerComponent> => {
	const animationHandler = animations && AnimationHandler(animations)

	const getMenuTransitionChildren = (menuContainerId: string) => {
		const menuContainerElement = window ? domSelectors.getByCompId(menuContainerId, window.document) : undefined

		if (window && menuContainerElement) {
			const children = Array.from(menuContainerElement.querySelectorAll('*'))

			return children.filter((child) => {
				const { transitionProperty, transitionDuration, transitionDelay } = window.getComputedStyle(child)

				return (
					(transitionProperty === 'all' || transitionProperty === 'visibility') &&
					(transitionDelay !== '0s' || transitionDuration !== '0s')
				)
			}) as Array<HTMLElement>
		}

		return []
	}

	return {
		componentType: 'MenuContainer',
		getComponentDriver: (viewerComponent) => {
			const onToggleCallbacks: Record<string, (isOpen: boolean) => void> = {}
			const getMenuAndToggleIds = () => {
				const { menuTogglesConfig } = pageConfig
				return [
					viewerComponent.id,
					...Object.keys(menuTogglesConfig).filter(
						(toggleId) => menuTogglesConfig[toggleId].menuContainerId === viewerComponent.id
					),
				]
			}
			let cyclicTabbingIds: Array<string> | undefined

			const animateIn = (animation: AnimationData) =>
				new Promise<void>((resolve) => {
					animationHandler!.animate(
						viewerComponent.id,
						{
							inBehavior: animation,
							outBehavior: { name: 'outBehavior' },
						},
						true,
						() => {
							resolve()
						}
					)
				})

			const toggle = (immediate: boolean = false, focusMenu: boolean = false) =>
				new Promise<void>((resolve) => {
					const shouldOpen = !viewerComponent.getProps().isOpen
					const shouldAnimate = !immediate && animationHandler
					const shouldUpdateVisibility = shouldOpen || !shouldAnimate
					const isMobile = viewMode === 'mobile'
					const focusMenuContainer = () => {
						if (!isSSR(window)) {
							viewerComponent.getCompRef().then((ref: any) => {
								ref?.focus()
							})
						}
					}

					if (shouldOpen) {
						cyclicTabbingIds = getMenuAndToggleIds()
						cyclicTabbing.enableCyclicTabbing(cyclicTabbingIds)
					} else {
						// Disabling matches the exact ids that were enabled, even if the config changed meanwhile.
						cyclicTabbing.disableCyclicTabbing(cyclicTabbingIds ?? getMenuAndToggleIds())
						cyclicTabbingIds = undefined
					}

					siteScrollBlocker && siteScrollBlocker.setSiteScrollingBlocked(shouldOpen, viewerComponent.id)
					if (shouldAnimate) {
						const menuTransitionChildren = getMenuTransitionChildren(viewerComponent.id)

						menuContainerHooks?.onAnimationStart()
						shouldOpen &&
							menuTransitionChildren.forEach((child) => {
								child.style.display = ''
							})

						animationHandler!.animate(
							viewerComponent.id,
							pageConfig.menuContainerConfig[viewerComponent.id].animations,
							shouldOpen,
							(reversed: boolean) => {
								menuContainerHooks?.onAnimationEnd()
								const shouldClose = reversed ? shouldOpen : !shouldOpen
								if (shouldClose) {
									viewerComponent.updateProps({
										isVisible: false,
									})
									menuTransitionChildren.forEach((child) => {
										child.style.display = 'none'
									})
								}
								resolve()
								focusMenu && focusMenuContainer()
							}
						)
					}

					Object.values(onToggleCallbacks).forEach((c) => c(shouldOpen))
					/* When we navigate from HamburgerMenu in responsive site, the menu is re-rendered.
					 * Both the navigation handler & onClick callback are called asynchronously (yieldToMain), leading to race condition.
					 * So we need to update the props only in case the comp is still in structure to prevent 'missing from structure' error
					 */
					setTimeout(() => {
						if (structureApi.get(viewerComponent.id)) {
							viewerComponent.updateProps({
								isOpen: shouldOpen,
								...(shouldUpdateVisibility && { isVisible: shouldOpen }),
							})
						}
					}, 0)
					if (shouldOpen) {
						const menuHeight =
							!isSSR(window) && isMobile
								? `calc(${window.getComputedStyle(document.body).height}/var(--zoom-factor, 1))`
								: '100vh'
						viewerComponent.updateStyle({
							'--menu-height': menuHeight,
						})
					}
					if (!shouldAnimate) {
						resolve()
						focusMenu && focusMenuContainer()
					}
				})

			const toggleIfNeeded = async (openMenu: boolean, immediate?: boolean) => {
				const isMenuOpen = viewerComponent.getProps().isOpen

				if (openMenu !== isMenuOpen) {
					await toggle(immediate)
				}
			}

			const api: MenuContainerDriver = {
				open: (immediate) => toggleIfNeeded(true, immediate),
				toggle,
				onToggle: (callback) => {
					const callbackId = _.uniqueId('callback')
					onToggleCallbacks[callbackId] = callback
					return () => delete onToggleCallbacks[callbackId]
				},
				close: (immediate) => toggleIfNeeded(false, immediate),
				animateIn,
				unblockScroll: () => siteScrollBlocker && siteScrollBlocker.setSiteScrollingBlocked(false, viewerComponent.id),
			}

			return api as any
		},
	}
}

export const MenuContainerDriverFactory = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		ViewModeSym,
		BrowserWindowSymbol,
		CyclicTabbingSymbol,
		StructureAPI,
		DomSelectorsSymbol,
		optional(SiteScrollBlockerSymbol),
		optional(Animations),
		optional(MenuContainerHooksSymbol),
	],
	menuContainerDriverFactory
)
